import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type LongRunningAnalysisStage =
  'waiting' | 'fetching' | 'analyzing' | 'upgrading-and-analyzing' | 'finished' | 'failed';

export interface ComponentDesignAnalysisState {
  readonly currentStage: LongRunningAnalysisStage;

  readonly recordedMessages: {
    readonly stage: LongRunningAnalysisStage;
    readonly type: 'warning' | 'error';
    readonly message: string;
  }[];
}

export interface LongRunningAnalysisOperation {
  readonly canceled: boolean;
  readonly componentDesigns: Record<string, ComponentDesignAnalysisState>;
}

export interface LongRunningUploadOperation {
  readonly canceled: boolean;
  readonly finalized: boolean;

  readonly allComponentNames: string[];
  readonly alreadyUploadedComponentNames: string[];
  readonly currentlyUploadingComponentNames: string[];

  readonly errors: Record<string, string[]>;
  readonly warnings: Record<string, string[]>;
}

type LongRunningOperationsState = {
  readonly analysis: LongRunningAnalysisOperation | null;
  readonly upload: LongRunningUploadOperation | null;
};

const initialState: LongRunningOperationsState = { analysis: null, upload: null };

const longRunningOperationsSlice = createSlice({
  name: 'longRunningOperations',
  initialState,
  reducers: {
    startLongRunningAnalysis: (state, action: PayloadAction<{ componentDesignGuids: string[] }>) => {
      const componentDesignStates: Record<string, ComponentDesignAnalysisState> = {};
      for (const componentDesignGuid of action.payload.componentDesignGuids) {
        componentDesignStates[componentDesignGuid] = {
          currentStage: 'waiting',
          recordedMessages: [],
        };
      }

      state.analysis = {
        canceled: false,
        componentDesigns: componentDesignStates,
      };
    },
    updateComponentDesignStateInLongRunningAnalysis: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; stage: LongRunningAnalysisStage }>,
    ) => {
      const { componentDesignGuid, stage } = action.payload;

      if (state.analysis != null && state.analysis.componentDesigns[componentDesignGuid] != null) {
        state.analysis.componentDesigns[componentDesignGuid].currentStage = stage;
      }
    },
    recordErrorMessageDuringLongRunningAnalysis: (
      state,
      action: PayloadAction<{
        componentDesignGuid: string;
        fatal: boolean;
        type: 'warning' | 'error';
        message: string;
      }>,
    ) => {
      const { componentDesignGuid, fatal, type, message } = action.payload;

      if (state.analysis != null && state.analysis.componentDesigns[componentDesignGuid] != null) {
        const componentDesignAnalysisState = state.analysis.componentDesigns[componentDesignGuid];
        componentDesignAnalysisState.recordedMessages.push({
          stage: componentDesignAnalysisState.currentStage,
          type,
          message,
        });

        if (fatal) {
          componentDesignAnalysisState.currentStage = 'failed';
        }
      }
    },
    cancelLongRunningAnalysis: (state) => {
      if (state.analysis != null) {
        state.analysis.canceled = true;
      }
    },
    clearLongRunningAnalysis: (state) => {
      state.analysis = null;
    },
    startLongRunningUpload: (state, action: PayloadAction<{ componentDesignNames: string[] }>) => {
      state.upload = {
        canceled: false,
        finalized: false,

        allComponentNames: action.payload.componentDesignNames,
        alreadyUploadedComponentNames: [],
        currentlyUploadingComponentNames: [],

        errors: {},
        warnings: {},
      };
    },
    startUploadingComponentDesignInLongRunningUpload: (state, action: PayloadAction<{ name: string }>) => {
      if (state.upload != null) {
        if (!state.upload.currentlyUploadingComponentNames.includes(action.payload.name)) {
          state.upload.currentlyUploadingComponentNames.push(action.payload.name);
        }
      }
    },
    finishUploadingComponentDesignInLongRunningUpload: (
      state,
      action: PayloadAction<{ readonly name: string; errors: readonly string[]; readonly warnings: readonly string[] }>,
    ) => {
      if (state.upload != null) {
        const index = state.upload.currentlyUploadingComponentNames.indexOf(action.payload.name);
        if (index !== -1) {
          state.upload.currentlyUploadingComponentNames.splice(index, 1);
        }

        if (!state.upload.alreadyUploadedComponentNames.includes(action.payload.name)) {
          state.upload.alreadyUploadedComponentNames.push(action.payload.name);
        }

        if (action.payload.errors.length > 0) {
          if (action.payload.name in state.upload.errors) {
            for (const errorMessage of action.payload.errors) {
              state.upload.errors[action.payload.name].push(errorMessage);
            }
          } else {
            state.upload.errors[action.payload.name] = [...action.payload.errors];
          }
        }

        if (action.payload.warnings.length > 0) {
          if (action.payload.name in state.upload.warnings) {
            for (const warningMessage of action.payload.warnings) {
              state.upload.warnings[action.payload.name].push(warningMessage);
            }
          } else {
            state.upload.warnings[action.payload.name] = [...action.payload.warnings];
          }
        }
      }
    },
    finalizeLongRunningUpload: (state) => {
      if (state.upload != null) {
        state.upload.finalized = true;
      }
    },
    concludeLongRunningUpload: (state) => {
      state.upload = null;
    },
  },
});

export const {
  startLongRunningAnalysis,
  updateComponentDesignStateInLongRunningAnalysis,
  recordErrorMessageDuringLongRunningAnalysis,
  cancelLongRunningAnalysis,
  clearLongRunningAnalysis,
  startLongRunningUpload,
  startUploadingComponentDesignInLongRunningUpload,
  finishUploadingComponentDesignInLongRunningUpload,
  finalizeLongRunningUpload,
  concludeLongRunningUpload,
} = longRunningOperationsSlice.actions;
export const longRunningOperations = longRunningOperationsSlice.reducer;
